/*
 * Ritual — custom elements layered on Dawn 16.
 *   <r-countdown>   sale timers (evergreen / fixed date / midnight), synced page-wide
 *   <r-carousel>    scroll-snap carousel with prev/next + dots
 *   <r-bundle>      product page bundle/quantity offer picker
 *   <r-sticky-atc>  sticky add-to-cart bar for the main product form
 * Depends on Dawn's constants.js + pubsub.js (loaded before this file).
 */

(() => {
  const settings = window.ritualSettings || {};
  const STORAGE_KEY = 'ritual:countdown-end';

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* storage blocked — timer still works for this page view */
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {
        /* storage blocked */
      }
    },
  };

  /* ---------------- Countdown ---------------- */

  let sharedEnd = null;

  function resolveEnd(mode) {
    const now = Date.now();
    if (mode === 'fixed') {
      const parsed = settings.countdownEnd ? Date.parse(settings.countdownEnd) : NaN;
      if (!Number.isNaN(parsed)) return { end: parsed, restart: false };
      mode = 'evergreen';
    }
    if (mode === 'midnight') {
      const d = new Date();
      d.setHours(24, 0, 0, 0);
      return { end: d.getTime(), restart: true };
    }
    const minutes = Number(settings.countdownMinutes) || 900;
    if (sharedEnd && sharedEnd > now) return { end: sharedEnd, restart: true };
    const stored = Number(storage.get(STORAGE_KEY));
    if (stored && stored > now) {
      sharedEnd = stored;
    } else {
      sharedEnd = now + minutes * 60 * 1000;
      storage.set(STORAGE_KEY, String(sharedEnd));
    }
    return { end: sharedEnd, restart: true };
  }

  const pad = (n) => String(n).padStart(2, '0');

  class RitualCountdown extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || settings.countdownMode || 'evergreen';
      const { end, restart } = resolveEnd(this.mode);
      this.end = end;
      this.restart = restart;
      this.units = {
        d: this.querySelector('[data-unit="d"]'),
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]'),
      };
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      let diff = this.end - Date.now();
      if (diff <= 0) {
        if (this.restart) {
          if (this.mode !== 'midnight') {
            sharedEnd = null;
            storage.set(STORAGE_KEY, '');
          }
          this.end = resolveEnd(this.mode).end;
          diff = this.end - Date.now();
        } else {
          window.clearInterval(this.timer);
          diff = 0;
          if (settings.countdownHideExpired) {
            const wrapper = this.closest('[data-countdown-wrapper]');
            (wrapper || this).hidden = true;
          }
        }
      }
      const total = Math.max(0, Math.floor(diff / 1000));
      const days = Math.floor(total / 86400);
      const hours = Math.floor((total % 86400) / 3600);
      const mins = Math.floor((total % 3600) / 60);
      const secs = total % 60;
      if (this.units.d) {
        this.units.d.textContent = pad(days);
        if (this.units.h) this.units.h.textContent = pad(hours);
      } else if (this.units.h) {
        this.units.h.textContent = pad(hours + days * 24);
      }
      if (this.units.m) this.units.m.textContent = pad(mins);
      if (this.units.s) this.units.s.textContent = pad(secs);
    }
  }

  if (!customElements.get('r-countdown')) customElements.define('r-countdown', RitualCountdown);

  /* ---------------- Carousel ---------------- */

  class RitualCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.slides = Array.from(this.track.children);

      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

      if (this.dotsWrap && this.slides.length > 1) {
        this.dotsWrap.innerHTML = '';
        this.dots = this.slides.map((slide, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'r-carousel__dot';
          b.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
          b.addEventListener('click', () => this.scrollToSlide(i));
          this.dotsWrap.appendChild(b);
          return b;
        });
      }

      const autoplay = Number(this.dataset.autoplay);
      if (autoplay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.autoplayTimer = window.setInterval(() => {
          if (this.matches(':hover') || this.contains(document.activeElement)) return;
          const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
          atEnd ? this.scrollToSlide(0) : this.go(1);
        }, autoplay * 1000);
      }

      this.update();
    }

    disconnectedCallback() {
      window.clearInterval(this.autoplayTimer);
    }

    slideStep() {
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    go(dir) {
      const perView = Math.max(1, Math.round(this.track.clientWidth / this.slideStep()));
      this.track.scrollBy({ left: dir * this.slideStep() * perView, behavior: 'smooth' });
    }

    scrollToSlide(i) {
      const slide = this.slides[i];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
    }

    onScroll() {
      window.cancelAnimationFrame(this.raf);
      this.raf = window.requestAnimationFrame(() => this.update());
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 4;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      if (this.dots) {
        const index = Math.round(this.track.scrollLeft / this.slideStep());
        this.dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
      }
    }
  }

  if (!customElements.get('r-carousel')) customElements.define('r-carousel', RitualCarousel);

  /* ---------------- Bundle picker ---------------- */

  class RitualBundle extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (event) => {
        if (event.target.matches('input[data-bundle-option]')) this.apply();
      });
      this.apply();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== this.sectionId || !data.html) return;
          const fresh = data.html.getElementById(this.id);
          if (!fresh) return;
          const selected = this.selectedIndex();
          this.innerHTML = fresh.innerHTML;
          const inputs = this.options();
          if (inputs[selected]) inputs[selected].checked = true;
          this.apply();
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    options() {
      return Array.from(this.querySelectorAll('input[data-bundle-option]'));
    }

    selectedIndex() {
      return Math.max(
        0,
        this.options().findIndex((i) => i.checked)
      );
    }

    apply() {
      const input = this.options().find((i) => i.checked) || this.options()[0];
      if (!input) return;
      const qty = input.dataset.quantity || '1';

      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.contains(input));
      });

      const formId = this.dataset.formId;
      document.querySelectorAll(`input[name="quantity"][form="${formId}"]`).forEach((q) => {
        q.value = qty;
      });

      const set = (selector, value) => {
        const el = this.querySelector(selector);
        if (!el) return;
        el.innerHTML = value || '';
        el.hidden = !value;
      };
      set('[data-bundle-price]', input.dataset.price);
      set('[data-bundle-compare]', input.dataset.compare);
      set('[data-bundle-save]', input.dataset.save);
      set('[data-bundle-unit]', input.dataset.unit);

      const atcPrice = document.querySelector(`#ProductSubmitButton-${this.sectionId} [data-r-atc-price]`);
      if (atcPrice) {
        atcPrice.innerHTML = input.dataset.price ? `<span aria-hidden="true">•</span> ${input.dataset.price}` : '';
        atcPrice.hidden = !input.dataset.price;
      }

      if (Number(qty) >= 2) this.applyDiscountCode();

      document.dispatchEvent(
        new CustomEvent('ritual:bundle-change', {
          detail: {
            sectionId: this.sectionId,
            quantity: qty,
            label: input.dataset.label,
            price: input.dataset.price,
          },
        })
      );
    }
  }

  // Multi-unit offers are priced by a discount code; put it on the cart once so checkout honours the offer price.
  RitualBundle.prototype.applyDiscountCode = function () {
    const code = this.dataset.discountCode;
    if (!code || RitualBundle.appliedCode === code) return;
    RitualBundle.appliedCode = code;
    const root = (window.routes && window.routes.cart_url) || '/cart';
    fetch(`${root}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    }).catch(() => {
      RitualBundle.appliedCode = null;
    });
  };

  if (!customElements.get('r-bundle')) customElements.define('r-bundle', RitualBundle);

  /* ---------------- Sticky add to cart ---------------- */

  class RitualStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal, .quick-add-modal, product-modal')) {
        this.remove();
        return;
      }
      this.sectionId = this.dataset.sectionId;
      this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!this.mainButton) return;
      this.cta = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');

      this.cta?.addEventListener('click', () => {
        const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!button) return;
        if (button.disabled || this.dataset.action === 'scroll') {
          button.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        button.click();
      });

      this.ctaPrice = this.querySelector('[data-sticky-cta-price]');
      const showBundle = (label, price) => {
        if (this.label && label) this.label.textContent = label;
        if (this.price && price) this.price.innerHTML = price;
        if (this.ctaPrice && price) {
          this.ctaPrice.innerHTML = `<span aria-hidden="true">•</span> ${price}`;
          this.ctaPrice.hidden = false;
        }
      };
      document.addEventListener('ritual:bundle-change', (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        showBundle(event.detail.label, event.detail.price);
      });
      // The bundle may have announced its default choice before this bar connected
      const current = document.querySelector(
        `r-bundle[data-section-id="${this.sectionId}"] input[data-bundle-option]:checked`
      );
      if (current) showBundle(current.dataset.label, current.dataset.price);

      this.observer = new IntersectionObserver(
        ([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
          this.setAttribute('aria-hidden', passed ? 'false' : 'true');
          if (this.cta) this.cta.tabIndex = passed ? 0 : -1;
        },
        { threshold: 0 }
      );
      this.observer.observe(this.mainButton);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  if (!customElements.get('r-sticky-atc')) customElements.define('r-sticky-atc', RitualStickyAtc);

  /* ---------------- Free gift sync ----------------
   * Theme settings → Ritual → Free gifts define rules: { variant, trigger, min }.
   * When the cart holds >= min units of the trigger product, exactly one gift line
   * (marked with the hidden _ritual_gift property) is kept in the cart; otherwise it
   * is removed. The gift is made free by a matching automatic "Buy X get Y" discount.
   */

  const GIFT_SOURCE = 'ritual-gifts';
  const giftRules = (settings.gifts || []).filter((r) => r && r.variant && r.trigger);
  let giftSyncing = null;
  let giftQueued = false;

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`${url} ${response.status}`);
    return response.json();
  }

  async function syncGifts() {
    const root = (window.routes && window.routes.cart_url) || '/cart';
    const cart = await (await fetch(`${root}.js`, { headers: { Accept: 'application/json' } })).json();
    let changed = false;

    for (const rule of giftRules) {
      const tag = String(rule.variant);
      const triggerQty = cart.items
        .filter((item) => item.product_id === rule.trigger && !(item.properties && item.properties._ritual_gift))
        .reduce((sum, item) => sum + item.quantity, 0);
      const giftLines = cart.items.filter(
        (item) => item.variant_id === rule.variant && item.properties && item.properties._ritual_gift === tag
      );
      const want = triggerQty >= rule.min ? 1 : 0;
      const have = giftLines.reduce((sum, item) => sum + item.quantity, 0);
      if (have === want && giftLines.length <= 1) continue;

      // Remove surplus lines, keep at most one with quantity 1.
      for (let i = 0; i < giftLines.length; i++) {
        const quantity = i === 0 ? want : 0;
        if (giftLines[i].quantity !== quantity) {
          await cartRequest(`${root}/change.js`, { id: giftLines[i].key, quantity });
          changed = true;
        }
      }
      if (want && giftLines.length === 0 && rule.available !== false) {
        try {
          await cartRequest(`${root}/add.js`, {
            items: [{ id: rule.variant, quantity: 1, properties: { _ritual_gift: tag } }],
          });
          changed = true;
        } catch (e) {
          /* gift sold out or unpublished — leave the cart as is */
        }
      }
    }
    return changed;
  }

  async function refreshDrawer() {
    const root = (window.routes && window.routes.cart_url) || '/cart';
    const [drawerHtml, bubbleHtml] = await Promise.all([
      fetch(`${root}?section_id=cart-drawer`).then((r) => r.text()),
      fetch(`${root}?section_id=cart-icon-bubble`).then((r) => r.text()),
    ]);
    const parser = new DOMParser();
    const drawer = document.querySelector('cart-drawer');
    const freshInner = parser.parseFromString(drawerHtml, 'text/html').querySelector('.drawer__inner');
    const inner = drawer && drawer.querySelector('.drawer__inner');
    if (inner && freshInner) {
      inner.innerHTML = freshInner.innerHTML;
      const empty = freshInner.querySelector('.drawer__inner-empty') !== null;
      drawer.classList.toggle('is-empty', empty);
      inner.classList.toggle('is-empty', empty);
    }
    const bubble = document.getElementById('cart-icon-bubble');
    const freshBubble = parser.parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
  }
  window.ritualRefreshDrawer = refreshDrawer;

  function refreshCartUI() {
    const cartPageItems = document.querySelector('cart-items');
    if (cartPageItems && document.getElementById('main-cart-footer')) {
      window.location.reload();
      return;
    }
    refreshDrawer().catch(() => {});
  }

  function requestGiftSync() {
    if (!giftRules.length) return;
    if (giftSyncing) {
      giftQueued = true;
      return;
    }
    giftSyncing = syncGifts()
      .then((changed) => changed && refreshCartUI())
      .catch(() => {})
      .finally(() => {
        giftSyncing = null;
        if (giftQueued) {
          giftQueued = false;
          requestGiftSync();
        }
      });
  }

  if (giftRules.length) {
    const start = () => {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === GIFT_SOURCE) return;
          requestGiftSync();
        });
      }
      requestGiftSync();
    };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  }
  /* ---------------- Cart reservation timer ---------------- */

  const TIMER_KEY = 'ritual:cart-reserved-until';

  class RitualCartTimer extends HTMLElement {
    connectedCallback() {
      const minutes = Number(this.dataset.minutes) || 5;
      const token = this.dataset.cartToken || '';
      const now = Date.now();
      let saved = null;
      try {
        saved = JSON.parse(storage.get(TIMER_KEY) || 'null');
      } catch (e) {
        saved = null;
      }
      // One reservation per cart. A new cart, or a drawer opened after the last
      // reservation ran out, starts a fresh countdown instead of opening expired.
      let until = saved && typeof saved === 'object' && saved.token === token ? Number(saved.until) : 0;
      if (!until || until <= now) {
        until = now + minutes * 60 * 1000;
        storage.set(TIMER_KEY, JSON.stringify({ token, until }));
      }
      this.until = until;
      this.timeEl = this.querySelector('[data-timer-time]');
      this.labelEl = this.querySelector('[data-timer-label]');
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const left = Math.max(0, Math.floor((this.until - Date.now()) / 1000));
      if (left === 0) {
        window.clearInterval(this.timer);
        if (this.labelEl) this.labelEl.textContent = this.dataset.expired || '';
        if (this.timeEl) this.timeEl.textContent = '';
        this.classList.add('is-expired');
        return;
      }
      if (this.timeEl) this.timeEl.textContent = `${pad(Math.floor(left / 60))}:${pad(left % 60)}`;
    }
  }

  if (!customElements.get('r-cart-timer')) customElements.define('r-cart-timer', RitualCartTimer);

  // Emptying the cart ends the reservation, so the next item starts a new one.
  const watchEmptyCart = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cartData && event.cartData.item_count === 0) storage.remove(TIMER_KEY);
    });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', watchEmptyCart) : watchEmptyCart();

  /* ---------------- Footer menus: accordions on mobile, open on desktop ---------------- */

  const footerAccordions = () => {
    const desktop = window.matchMedia('(min-width: 750px)');
    const sync = () => {
      document.querySelectorAll('.r-footer-acc').forEach((details) => {
        details.open = desktop.matches;
      });
    };
    sync();
    desktop.addEventListener?.('change', sync);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', footerAccordions) : footerAccordions();

  /* ---------------- Shipping protection toggle ---------------- */

  class RitualProtectionToggle extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="checkbox"]');
      this.input?.addEventListener('change', () => this.onChange());
    }

    async onChange() {
      const root = (window.routes && window.routes.cart_url) || '/cart';
      this.input.disabled = true;
      this.classList.add('is-loading');
      try {
        if (this.input.checked) {
          await cartRequest(`${root}/add.js`, { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] });
        } else if (this.dataset.lineKey) {
          await cartRequest(`${root}/change.js`, { id: this.dataset.lineKey, quantity: 0 });
        }
      } catch (e) {
        this.input.checked = !this.input.checked;
      }
      await refreshDrawer().catch(() => {});
    }
  }

  if (!customElements.get('r-protection-toggle')) customElements.define('r-protection-toggle', RitualProtectionToggle);
})();
